/**
 * ClientAdmin Landing Page
 * Scoped console for clients' own org:admin users.
 * Fetches the client profile, then renders the email sources management UI
 * (if enabled) or a locked state message (if not).
 */
import { useCallback, useEffect, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { EmailSourcesTab } from "@/pages/admin/components/EmailSourcesTab";
import { useClientAdminApi } from "@/services/api/client-admin-api";
import type { ClientAdminProfileDto } from "@/types/api/client-admin";
import { ClientAdminShell } from "./ClientAdminShell";

export default function ClientAdminPage() {
    const clientAdminApi = useClientAdminApi();
    const [profile, setProfile] = useState<ClientAdminProfileDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await clientAdminApi.getProfile();
            setProfile(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load profile.");
        } finally {
            setIsLoading(false);
        }
    }, [clientAdminApi]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    if (isLoading) {
        return (
            <ClientAdminShell>
                <div className="flex h-full items-center justify-center">
                    <LoadingIndicator size="lg" label="Loading..." />
                </div>
            </ClientAdminShell>
        );
    }

    if (error || !profile) {
        return (
            <ClientAdminShell>
                <div className="flex h-full items-center justify-center">
                    <p className="text-sm text-error-primary">{error || "Failed to load profile."}</p>
                </div>
            </ClientAdminShell>
        );
    }

    return (
        <ClientAdminShell>
            <div className="mx-auto w-full max-w-[90rem] px-6 py-8">
                <h1 className="mb-6 text-display-xs font-semibold text-primary">{profile.clientName}</h1>
                {profile.isEmailIngestionEnabled ? (
                    <EmailSourcesTab clientId={profile.clientId} />
                ) : (
                    <div className="py-12 text-center">
                        <p className="text-sm text-tertiary">
                            Mailbox management is not enabled for your account. Contact your ACOM administrator to enable this feature.
                        </p>
                    </div>
                )}
            </div>
        </ClientAdminShell>
    );
}
